import React from 'react';
import { FileText } from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';

const fmtDate = (d, opts = { month: 'short', day: 'numeric', year: 'numeric' }) =>
  new Date(d).toLocaleDateString('en-US', opts);

export const DocumentsTab = ({ clientDocs, loadingDocs }) => {
  if (loadingDocs) {
    return (
      <div className="mt-4">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500" />
        </div>
      </div>
    );
  }

  if (clientDocs.length === 0) {
    return (
      <div className="mt-4">
        <div className="text-center py-8 text-gray-400">
          <FileText className="h-10 w-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No documents published to this client yet</p>
          <p className="text-xs mt-1">Go to <strong>Documents</strong> in the sidebar to publish templates</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="space-y-3">
        {clientDocs.map((doc) => {
          const isExpired = doc.is_expired;
          const borderClass = isExpired
            ? 'border-red-200 bg-red-50/50'
            : doc.status === 'acknowledged'
              ? 'border-green-200 bg-green-50/50'
              : 'border-amber-200 bg-amber-50/50';
          const iconColor = isExpired ? 'text-red-600' : doc.status === 'acknowledged' ? 'text-green-600' : 'text-amber-600';
          const iconBg = isExpired ? 'bg-red-100' : doc.status === 'acknowledged' ? 'bg-green-100' : 'bg-amber-100';

          return (
            <div key={doc.id} className={`p-4 rounded-lg border ${borderClass}`} data-testid={`client-doc-${doc.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`p-1.5 rounded-md mt-0.5 ${iconBg}`}>
                    <FileText className={`h-4 w-4 ${iconColor}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{doc.title}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {doc.is_addendum && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">Addendum</Badge>
                      )}
                      <Badge className={`text-xs ${
                        doc.template_type === 'terms_and_conditions'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-indigo-100 text-indigo-700'
                      }`}>
                        {doc.template_type === 'terms_and_conditions' ? 'T&C' : doc.template_type.replace(/_/g, ' ')}
                      </Badge>
                      <span className="text-xs text-gray-400">v{doc.version}</span>
                    </div>
                    {doc.addendum_summary && (
                      <p className="text-xs text-gray-500 mt-1 italic">{doc.addendum_summary}</p>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {isExpired ? (
                    <div>
                      <Badge className="bg-red-100 text-red-700 text-xs">Expired</Badge>
                      <p className="text-xs text-gray-400 mt-1">{fmtDate(doc.expires_at)}</p>
                    </div>
                  ) : doc.status === 'acknowledged' ? (
                    <div>
                      <Badge className="bg-green-100 text-green-700 text-xs">Acknowledged</Badge>
                      <p className="text-xs text-gray-400 mt-1">{fmtDate(doc.acknowledged_at)}</p>
                      <p className="text-xs text-gray-500">{doc.acknowledged_by_name}</p>
                      {doc.expires_at && (
                        <p className="text-xs text-blue-500 mt-0.5">Exp: {fmtDate(doc.expires_at)}</p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <Badge className="bg-amber-100 text-amber-700 text-xs">Pending</Badge>
                      <p className="text-xs text-gray-400 mt-1">Sent {fmtDate(doc.created_at, { month: 'short', day: 'numeric' })}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
